import React, { useLayoutEffect, useRef, useState } from 'react'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material'
import api from '../../services/api'
import useAuth from '../../hooks/useAuth'
import { canEditProduct } from './productPermissions'

const failureMessage = (status) => {
  if (status === 400) return 'No se pudo guardar. Revisa los campos del producto.'
  if (status === 401 || status === 403) return 'La sesión no permite editar este producto. Cierra el formulario y revisa tu acceso.'
  if (status === 409) return 'No se pudo guardar por un conflicto. Cierra el formulario y recarga el detalle.'
  return 'No fue posible confirmar el guardado. Cierra el formulario y recarga el detalle antes de reintentar.'
}

const ProductEditDialog = ({ product, productId, onClose, onSaved }) => {
  const { user } = useAuth()
  const [context] = useState(() => ({ user, product, productId, clientId: user?.clientId }))
  const [name, setName] = useState(product.name_product ?? '')
  const [description, setDescription] = useState(product.description ?? '')
  const [price, setPrice] = useState(product.price == null ? '' : String(product.price))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [blocked, setBlocked] = useState(false)
  const pending = useRef(false)
  const current = useRef(false)
  const permitted = context.user === user && context.clientId === user?.clientId &&
    context.product === product && context.productId === productId &&
    String(product.id) === String(productId) && canEditProduct(user, product)
  useLayoutEffect(() => {
    current.current = permitted
    return () => { current.current = false }
  }, [permitted])

  const submit = async (event) => {
    event.preventDefault()
    if (pending.current || !permitted || blocked) return
    if (typeof name !== 'string' || !name.trim() || name.trim().length > 255 ||
      typeof description !== 'string' || (price !== '' && !/^\d{1,8}(\.\d{1,2})?$/.test(price))) {
      setError('Indica un nombre de hasta 255 caracteres y un precio válido con hasta dos decimales.')
      return
    }
    pending.current = true
    setSaving(true)
    setError('')
    try {
      const response = await api.put(`/products/${productId}`, {
        name_product: name.trim(), description, price: price === '' ? null : price,
      })
      if (!current.current) return
      if (response.status !== 200) {
        setError(failureMessage(response.status))
        setBlocked(true)
        return
      }
      // The parent reloads GET; neither the PUT body nor response is a detail cache.
      onSaved()
    } catch (failure) {
      if (!current.current) return
      const status = failure?.response?.status
      setError(failureMessage(status))
      setBlocked(status !== 400)
    } finally {
      pending.current = false
      if (current.current) setSaving(false)
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="edit-product-title">
      <form onSubmit={submit} noValidate>
        <DialogTitle id="edit-product-title">Editar producto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!permitted && <Alert severity="warning">El contexto cambió. Cierra y vuelve a abrir el formulario.</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <TextField autoFocus required label="Nombre" value={name} onChange={(event) => setName(event.target.value)} disabled={saving || !permitted || blocked} inputProps={{ maxLength: 255 }} />
            <TextField label="Descripción" multiline value={description} onChange={(event) => setDescription(event.target.value)} disabled={saving || !permitted || blocked} />
            <TextField label="Precio" value={price} onChange={(event) => setPrice(event.target.value)} disabled={saving || !permitted || blocked} inputProps={{ inputMode: 'decimal' }} helperText="Opcional; usa punto decimal." />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button type="submit" disabled={saving || !permitted || blocked} variant="contained">{saving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
export default ProductEditDialog
